import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { GqlServiceV2 } from '../../modules/content/service/gql.service-v2';
import { filterFrag } from '../types/filter.types';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { DocumentMatchGridObject } from '@libs/types/src/deska-monitor/object-type/document-match.object';
import { NoticeDocumentGridObject } from '@libs/types/src/deska-monitor/object-type/notice-document.object';
import {
    ParcelImportGridObject,
    ParcelImportObject,
} from '@libs/types/src/deska-monitor/object-type/parcel-import.object';
import { WatchedParcelGridObject } from '@libs/types/src/deska-monitor/object-type/watched-parcel.object';
import {
    SearchKeywordGridObject,
    SearchKeywordObject,
} from '@libs/types/src/deska-monitor/object-type/search-keyword.object';
import { ParcelImportImportInput } from '@libs/types/src/deska-monitor/input-type/parcel-import.import-input';
import { SearchKeywordCreateInput } from '@libs/types/src/deska-monitor/input-type/search-keyword.create-input';
import { SearchKeywordUpdateInput } from '@libs/types/src/deska-monitor/input-type/search-keyword.update-input';
import {
    DigestRecipientGridObject,
    DigestRecipientObject,
} from '@libs/types/src/deska-monitor/object-type/digest-recipient.object';
import { DigestRecipientCreateInput } from '@libs/types/src/deska-monitor/input-type/digest-recipient.create-input';
import { DigestRecipientUpdateInput } from '@libs/types/src/deska-monitor/input-type/digest-recipient.update-input';
import { IngestRunGridObject } from '@libs/types/src/deska-monitor/object-type/ingest-run.object';
import { DigestRunGridObject } from '@libs/types/src/deska-monitor/object-type/digest-run.object';
import { MonitoredDashboardGridObject } from '@libs/types/src/deska-monitor/object-type/monitored-dashboard.object';

export const deskaMonitorMatchFrag = `
    id
    documentId
    type
    matchedValue
    katuzeKod
    katuzeNazev
    snippet
    digestRunId
    createdAt
    documentName
    dashboardName
    publishedAt
    edeskyUrl
    origUrl
`;

export const deskaMonitorDocumentFrag = `
    id
    edeskyDocId
    dashboardEdeskyId
    dashboardName
    name
    publishedAt
    edeskyUrl
    edeskyTextUrl
    origUrl
    hasOcrText
`;

export const deskaMonitorImportFrag = `
    id
    fileName
    filePath
    fileId
    status
    uploadedBy
    parcelsCount
    katuzeCount
    error
`;

export const deskaMonitorParcelFrag = `
    id
    katuzeKod
    katuzeNazev
    kmenoveCislo
    poddeleniCisla
    druhCislovani
    importId
`;

export const deskaMonitorKeywordFrag = `
    id
    value
    enabled
    regionIds
`;

export const deskaMonitorRegionFrag = `
    edeskyId
    name
`;

export const deskaMonitorRecipientFrag = `
    id
    email
    enabled
`;

export const deskaMonitorIngestRunFrag = `
    id
    startedAt
    finishedAt
    status
    searchedFrom
    keywordsUsed
    docsSeen
    docsMatched
    error
`;

export const deskaMonitorDigestRunFrag = `
    id
    periodFrom
    periodTo
    sentAt
    recipients
    documentsCount
    matchesCount
    status
    error
`;

@Injectable({ providedIn: 'root' })
export class DeskaMonitorGqlService extends GqlServiceV2 {
    public getDeskaMonitorMatches(
        input: GridInput,
        frag = deskaMonitorMatchFrag,
    ) {
        return this.query<DocumentMatchGridObject>(
            `
                query($input: GridInput!) {
                    documentMatch {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorDocuments(
        input: GridInput,
        frag = deskaMonitorDocumentFrag,
    ) {
        return this.query<NoticeDocumentGridObject>(
            `
                query($input: GridInput!) {
                    noticeDocument {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorImports(
        input: GridInput,
        frag = deskaMonitorImportFrag,
    ) {
        return this.query<ParcelImportGridObject>(
            `
                query($input: GridInput!) {
                    parcelImport {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public importDeskaMonitorParcels(
        input: ParcelImportImportInput,
        frag = deskaMonitorImportFrag,
    ) {
        return this.mutate<ParcelImportObject>(
            `
                mutation($input: ParcelImportImportInput!) {
                    parcelImport {
                        import(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorParcels(
        input: GridInput,
        frag = deskaMonitorParcelFrag,
    ) {
        return this.query<WatchedParcelGridObject>(
            `
                query($input: GridInput!) {
                    watchedParcel {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorKeywords(
        input: GridInput,
        frag = deskaMonitorKeywordFrag,
    ) {
        return this.query<SearchKeywordGridObject>(
            `
                query($input: GridInput!) {
                    searchKeyword {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    // kraje whose boards are monitored, as monitored dashboards of the kraj itself
    public getDeskaMonitorRegions(
        input: GridInput,
        frag = deskaMonitorRegionFrag,
    ) {
        return this.query<MonitoredDashboardGridObject>(
            `
                query($input: GridInput!) {
                    monitoredDashboard {
                        filter(input: $input) {
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createDeskaMonitorKeyword(
        input: SearchKeywordCreateInput,
        frag = deskaMonitorKeywordFrag,
    ) {
        return this.mutate<SearchKeywordObject>(
            `
                mutation($input: SearchKeywordCreateInput!) {
                    searchKeyword {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateDeskaMonitorKeyword(
        input: SearchKeywordUpdateInput,
        frag = deskaMonitorKeywordFrag,
    ) {
        return this.mutate<SearchKeywordObject>(
            `
                mutation($input: SearchKeywordUpdateInput!) {
                    searchKeyword {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteDeskaMonitorKeyword(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    searchKeyword {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorRecipients(
        input: GridInput,
        frag = deskaMonitorRecipientFrag,
    ) {
        return this.query<DigestRecipientGridObject>(
            `
                query($input: GridInput!) {
                    digestRecipient {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createDeskaMonitorRecipient(
        input: DigestRecipientCreateInput,
        frag = deskaMonitorRecipientFrag,
    ) {
        return this.mutate<DigestRecipientObject>(
            `
                mutation($input: DigestRecipientCreateInput!) {
                    digestRecipient {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateDeskaMonitorRecipient(
        input: DigestRecipientUpdateInput,
        frag = deskaMonitorRecipientFrag,
    ) {
        return this.mutate<DigestRecipientObject>(
            `
                mutation($input: DigestRecipientUpdateInput!) {
                    digestRecipient {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteDeskaMonitorRecipient(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    digestRecipient {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorIngestRuns(
        input: GridInput,
        frag = deskaMonitorIngestRunFrag,
    ) {
        return this.query<IngestRunGridObject>(
            `
                query($input: GridInput!) {
                    ingestRun {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeskaMonitorDigestRuns(
        input: GridInput,
        frag = deskaMonitorDigestRunFrag,
    ) {
        return this.query<DigestRunGridObject>(
            `
                query($input: GridInput!) {
                    digestRun {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public runDeskaMonitorIngest() {
        return this.mutate<OkObject>(
            `
                mutation {
                    noticeDocument {
                        runIngest {
                            ok
                        }
                    }
                }
            `,
        );
    }

    public sendDeskaMonitorDigest() {
        return this.mutate<OkObject>(
            `
                mutation {
                    digestRun {
                        sendNow {
                            ok
                        }
                    }
                }
            `,
        );
    }
}
